import { speechToTextSileroService } from "./speech-to-text-silero.service";
import {
  AUDIO_SERVICE_CONFIG,
  MEDIA_ERROR_TYPES,
  MEDIA_ERROR_MESSAGES,
} from "../common/constants/audio.constants";

/**
 * Service for managing audio recording, playback, and level monitoring
 */
class AudioService {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private isRecording = false;

  /**
   * Start audio recording with microphone access and level monitoring
   */
  async startRecording(): Promise<void> {
    try {
      await this.initializeMicrophone();
      this.setupAudioAnalyser();
      this.isRecording = true;
    } catch (error: any) {
      this.cleanup();
      throw this.createEnhancedError(error);
    }
  }

  /**
   * Stop audio recording and clean up resources
   */
  stopRecording(): void {
    if (this.isRecording) {
      this.cleanup();
    }
  }

  /**
   * Get current audio level normalized to 0-1 range
   */
  getAudioLevel(): number {
    if (!this.analyser || !this.isRecording) {
      return 0;
    }

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);

    const average = this.calculateAverage(dataArray);
    return average / AUDIO_SERVICE_CONFIG.NORMALIZATION_FACTOR;
  }

  /**
   * Play audio data from ArrayBuffer
   */
  async playAudioData(audioData: ArrayBuffer): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    try {
      const buffer = await this.audioContext.decodeAudioData(audioData);
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);
      source.start();
    } catch (error) {
      throw new Error("Failed to play audio data");
    }
  }

  /**
   * Mute audio by stopping recording and speech-to-text
   */
  mute(): void {
    // Just mute the service - DON'T stop recording to keep session warm
    speechToTextSileroService.setMuted(true);
  }

  /**
   * Initialize microphone with optimized audio constraints
   */
  private async initializeMicrophone(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: AUDIO_SERVICE_CONFIG.SAMPLE_RATE,
        channelCount: AUDIO_SERVICE_CONFIG.CHANNEL_COUNT,
      },
    });
  }

  /**
   * Setup audio analyser for level monitoring
   */
  private setupAudioAnalyser(): void {
    if (!this.stream) {
      throw new Error("Media stream not initialized");
    }

    this.audioContext = new AudioContext({
      sampleRate: AUDIO_SERVICE_CONFIG.SAMPLE_RATE,
    });

    const source = this.audioContext.createMediaStreamSource(this.stream);

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = AUDIO_SERVICE_CONFIG.FFT_SIZE;
    this.analyser.smoothingTimeConstant = AUDIO_SERVICE_CONFIG.SMOOTHING_TIME_CONSTANT;
    this.analyser.minDecibels = AUDIO_SERVICE_CONFIG.MIN_DECIBELS;
    this.analyser.maxDecibels = AUDIO_SERVICE_CONFIG.MAX_DECIBELS;

    source.connect(this.analyser);
  }

  /**
   * Calculate average value from Uint8Array
   */
  private calculateAverage(dataArray: Uint8Array): number {
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    return sum / dataArray.length;
  }

  /**
   * Clean up all audio resources
   */
  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyser = null;
    this.isRecording = false;
  }

  /**
   * Create enhanced error with user-friendly message
   */
  private createEnhancedError(error: any): Error {
    const errorMessage = this.getErrorMessage(error.name);

    const enhancedError = new Error(errorMessage);
    enhancedError.name = "MediaDeviceError";
    enhancedError.cause = error;

    return enhancedError;
  }

  /**
   * Get user-friendly error message based on error type
   */
  private getErrorMessage(errorName: string): string {
    if (MEDIA_ERROR_TYPES.NOT_FOUND.includes(errorName)) {
      return MEDIA_ERROR_MESSAGES.NOT_FOUND;
    }

    if (MEDIA_ERROR_TYPES.NOT_ALLOWED.includes(errorName)) {
      return MEDIA_ERROR_MESSAGES.NOT_ALLOWED;
    }

    if (MEDIA_ERROR_TYPES.NOT_READABLE.includes(errorName)) {
      return MEDIA_ERROR_MESSAGES.NOT_READABLE;
    }

    if (MEDIA_ERROR_TYPES.OVERCONSTRAINED.includes(errorName)) {
      return MEDIA_ERROR_MESSAGES.OVERCONSTRAINED;
    }

    return MEDIA_ERROR_MESSAGES.DEFAULT;
  }
}

export const audioService = new AudioService();
